import React, { createContext, useContext, useEffect, useState } from 'react';

// Map site TLD to European city coords (matches scrapingLocations in mock.js)
const SITE_TO_CITY = {
  'cultura.com': { city: 'Paris', country: 'France', x: 51, y: 50 },
  'conrad.de': { city: 'Berlin', country: 'Allemagne', x: 60, y: 42 },
  'giocattolieshop.it': { city: 'Rome', country: 'Italie', x: 58, y: 60 },
  'peragashop.it': { city: 'Rome', country: 'Italie', x: 58, y: 60 },
  'ufesa.es': { city: 'Madrid', country: 'Espagne', x: 43, y: 60 },
  'cofan.es': { city: 'Madrid', country: 'Espagne', x: 43, y: 60 },
  'dreams4kids.de': { city: 'Berlin', country: 'Allemagne', x: 60, y: 42 },
  'buyzoxs.de': { city: 'Amsterdam', country: 'Pays-Bas', x: 54, y: 44 },
  'caseking.de': { city: 'Bruxelles', country: 'Belgique', x: 53, y: 47 },
  'megajuguetes.es': { city: 'Lisbonne', country: 'Portugal', x: 38, y: 63 },
};

const SCRAPERS_INIT = [
  { id: 'S-01', site: 'cultura.com', country: 'FR', color: '#22d3ee', status: 'running', progress: 62, pages: 42, products: 128, latency: 340 },
  { id: 'S-02', site: 'conrad.de', country: 'DE', color: '#ec4899', status: 'running', progress: 34, pages: 21, products: 74, latency: 280 },
  { id: 'S-03', site: 'giocattolieshop.it', country: 'IT', color: '#a855f7', status: 'running', progress: 88, pages: 66, products: 210, latency: 410 },
  { id: 'S-04', site: 'ufesa.es', country: 'ES', color: '#f59e0b', status: 'queued', progress: 0, pages: 0, products: 0, latency: 0 },
];

const LOG_TEMPLATES = [
  '[GET] /category/{i} \u2192 200 OK ({ms}ms)',
  '[PARSE] extracted EAN 9782{i}{i}{i}{i}',
  '[PROXY] rotating pool \u2192 node-{i}',
  '[SNIFF] product card #{i} priced',
  '[EXTRACT] {i} items \u2192 buffer',
  '[HERMES] agent decision: continue',
  '[BOT] anti-fingerprint check pass',
  '[FLUSH] chunk {i} \u2192 CSV stream',
];

const genLog = (site) => {
  const t = LOG_TEMPLATES[Math.floor(Math.random() * LOG_TEMPLATES.length)];
  const i = Math.floor(Math.random() * 999);
  const ms = 120 + Math.floor(Math.random() * 480);
  return `${new Date().toISOString().slice(11, 19)} \u00b7 ${site.padEnd(20, ' ')} ${t.replaceAll('{i}', i).replace('{ms}', ms)}`;
};

const LiveContext = createContext(null);

export const LiveProvider = ({ children }) => {
  const [scrapers, setScrapers] = useState(SCRAPERS_INIT);
  const [logs, setLogs] = useState([]);
  const [featuredIdx, setFeaturedIdx] = useState(0);

  // Advance scrapers state
  useEffect(() => {
    const int = setInterval(() => {
      setScrapers((prev) => prev.map((s) => {
        if (s.status !== 'running') return s;
        const delta = Math.random() * 4;
        const np = Math.min(100, s.progress + delta);
        return {
          ...s,
          progress: np,
          pages: s.pages + (Math.random() > 0.55 ? 1 : 0),
          products: s.products + (Math.random() > 0.5 ? Math.floor(Math.random() * 3) : 0),
          latency: Math.max(150, s.latency + (Math.random() - 0.5) * 40),
          status: np >= 100 ? 'done' : 'running',
        };
      }));
    }, 900);
    return () => clearInterval(int);
  }, []);

  // Generate logs
  useEffect(() => {
    const int = setInterval(() => {
      setScrapers((current) => {
        const active = current.filter((s) => s.status === 'running');
        if (active.length === 0) return current;
        const s = active[Math.floor(Math.random() * active.length)];
        setLogs((l) => [genLog(s.site), ...l].slice(0, 40));
        return current;
      });
    }, 750);
    return () => clearInterval(int);
  }, []);

  // Rotate which scraper is featured on the globe (only among running scrapers)
  useEffect(() => {
    const int = setInterval(() => {
      setScrapers((current) => {
        const runningIdx = current
          .map((s, i) => (s.status === 'running' ? i : -1))
          .filter((i) => i !== -1);
        if (runningIdx.length === 0) return current;
        setFeaturedIdx((prev) => {
          const currentPos = runningIdx.indexOf(prev);
          const nextPos = (currentPos + 1) % runningIdx.length;
          return runningIdx[nextPos];
        });
        return current;
      });
    }, 4000);
    return () => clearInterval(int);
  }, []);

  // Compute featured location for globe
  const featuredScraper = scrapers[featuredIdx] || scrapers[0];
  const featuredLocation = SITE_TO_CITY[featuredScraper?.site] || SITE_TO_CITY['cultura.com'];

  return (
    <LiveContext.Provider value={{ scrapers, logs, featuredScraper, featuredLocation, SITE_TO_CITY }}>
      {children}
    </LiveContext.Provider>
  );
};

export const useLive = () => {
  const ctx = useContext(LiveContext);
  if (!ctx) throw new Error('useLive must be used within LiveProvider');
  return ctx;
};
